import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { http, inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Eyebrow } from "../components/Reveal";

export default function ProductPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add, setOpen } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [img, setImg] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setP(null); setErr(false); setImg(0); setSize(""); setQty(1);
    http.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="px-5 pt-40 text-center font-mono uppercase text-zinc-400">Product not found. <Link className="text-[#00FF66]" to="/">Back to shop</Link></div>;
  if (!p) return <div className="min-h-screen px-5 pt-40 font-mono uppercase text-zinc-400 md:px-12">Loading…</div>;

  const imgs = [p.image, p.secondary_image];
  const commit = (buy) => {
    if (!size) { toast.error("Pick a size first"); return; }
    add(p, size, qty);
    if (buy) nav("/checkout");
    else { toast.success(`${p.name} · ${size} added to bag`); setOpen(true); }
  };

  return (
    <section className="px-5 pb-24 pt-28 md:px-12 md:pt-32" data-testid="product-page">
      <div className="mx-auto max-w-[1600px]">
        <Link to="/" data-testid="back-to-shop" className="mb-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to catalogue</Link>
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="flex flex-col-reverse gap-4 md:flex-row lg:col-span-7">
            <div className="flex gap-3 md:flex-col">
              {imgs.map((s, i) => (
                <button key={s + i} data-testid={`thumb-${i}`} onClick={() => setImg(i)} className={`h-24 w-20 overflow-hidden border-2 ${img === i ? "border-[#00FF66]" : "border-transparent opacity-60"}`}>
                  <img src={s} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <div className="clip-notch relative aspect-[3/4] flex-1 overflow-hidden bg-[#18181C]">
              {imgs.map((s, i) => (
                <motion.img key={s + i} src={s} alt={p.name} initial={false} animate={{ opacity: img === i ? 1 : 0, scale: img === i ? 1 : 1.05 }} transition={{ duration: 0.6 }} className="absolute inset-0 h-full w-full object-cover" />
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <Eyebrow>{p.badge} · {p.category}</Eyebrow>
            <h1 className="mt-4 font-display text-6xl font-black uppercase leading-[0.85] md:text-8xl" data-testid="product-name">{p.name}</h1>
            <p className="mt-4 font-mono text-2xl font-bold" data-testid="product-price">{inr(p.price)}</p>
            <p className="mt-6 leading-relaxed text-zinc-300">{p.description}</p>
            <div className="mt-6 flex flex-wrap gap-2 font-mono text-xs uppercase">
              {[p.fabric, p.fit, p.color].map((t) => <span key={t} className="border border-white/15 px-3 py-1.5 text-zinc-300">{t}</span>)}
            </div>
            <p className="mt-10 font-mono text-xs uppercase tracking-widest text-zinc-400">Select size</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.sizes.map((s) => (
                <button key={s} data-testid={`size-select-${s}`} onClick={() => setSize(s)} className={`h-12 w-14 border font-mono text-sm font-bold transition-colors ${size === s ? "border-[#00FF66] bg-[#00FF66] text-black" : "border-white/20 hover:border-white"}`}>{s}</button>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center border border-white/20">
                <button data-testid="qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="p-4 hover:text-[#00FF66]"><Minus className="h-4 w-4" /></button>
                <span className="w-10 text-center font-mono" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-inc" onClick={() => setQty(Math.min(10, qty + 1))} className="p-4 hover:text-[#00FF66]"><Plus className="h-4 w-4" /></button>
              </div>
              <motion.button whileTap={{ scale: 0.96 }} data-testid="add-to-cart-btn" onClick={() => commit(false)} className="flex-1 border border-white py-4 font-display text-2xl font-black uppercase transition-colors hover:bg-white hover:text-black">Add to bag</motion.button>
            </div>
            <motion.button whileTap={{ scale: 0.97 }} data-testid="buy-now-btn" onClick={() => commit(true)} className="mt-3 w-full bg-[#00FF66] py-4 font-display text-2xl font-black uppercase text-black transition-colors hover:bg-white">Buy now</motion.button>
            <p className="mt-6 font-mono text-xs uppercase text-zinc-400">Free shipping · Ships in 3–5 days</p>
          </div>
        </div>
      </div>
    </section>
  );
}
